import React, { useEffect, useState, useCallback, useMemo } from 'react'

import { deleteImage, getHostGlanceEndpoint, testHostGlance } from './actions'
import { ImagesSelector } from './model'
import { listVolumeSnapshots } from 'app/plugins/openstack/components/storage/snapshots/actions'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import ExternalLink from 'core/components/ExternalLink'
import { routeManager } from 'core/utils/routes'
import { IHealthServiceDetailsPageTabs } from '../networks/model'
import Modal from 'core/elements/modal'
import Button from 'core/elements/button'
import Text from 'core/elements/Text'
import Checkbox from 'core/elements/input/Checkbox'
import Alert from 'core/components/Alert'
import LoadingTextWithSpinner from 'core/components/LoadingTextWithSpinner'
import { volumeSnapshotsByIdSelector } from 'app/plugins/openstack/components/storage/snapshots/selectors'
import useListAction from 'core/hooks/useListAction'
import { useSelector } from 'react-redux'
import useDeleteAction from 'core/hooks/useDeleteAction'
import ShowActionState, { ActionStateResult } from 'core/components/ShowActionState'
import Info from 'core/components/validatedForm/Info'
import { initializeResults, processResults } from 'app/plugins/openstack/components/images/helpers'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Grid from 'core/elements/grid'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import clsx from 'clsx'

interface DeleteImageProps {
  rows: any[]
  onClose: (value: boolean) => void
}

interface VolumeSnapshotsGridProps {
  volumeSnapshotResults: ActionStateResult[]
  rows: ImagesSelector[]
  imageResults: ActionStateResult[]
}

export const getVolumeSnapshotsGridData = ({
  volumeSnapshotResults,
  rows,
  imageResults,
}: VolumeSnapshotsGridProps) => {
  // Precompute a Set of successful image row IDs for O(1) lookup
  const successfulImageIds = new Set(
    imageResults?.filter((r) => r?.status === 'Success').map((r) => r?.id),
  )
  // Precompute a Map from snapshotId -> row for O(1) lookup
  const snapshotIdToRow = new Map(rows?.flatMap((r) => r?.snapshotIds?.map((id) => [id, r]) ?? []))
  return volumeSnapshotResults?.map((vs) => {
    const row = snapshotIdToRow.get(vs.id)
    return {
      ...vs,
      vmSnapshotName: row?.name ?? '',
      vmSnapshotDeleted: successfulImageIds.has(row?.id),
    }
  })
}

const ImageServiceStatusMessage = ({ glanceHostNotReachable }) => {
  if (glanceHostNotReachable) {
    return (
      <Info error>
        <ErrorMessage>
          <Text variant="body2">
            Image library host not reachable. Please check the image service health{' '}
            <ExternalLink
              url={routeManager.routes.openstack.health.details.path({
                tab: IHealthServiceDetailsPageTabs.ServiceHealth,
              })}
              target="_blank"
              rel="noopener noreferrer"
            >
              here
            </ExternalLink>
            .
          </Text>
        </ErrorMessage>
      </Info>
    )
  }

  return null
}
const pageSizeOptions = [3, 5, 10].map((value) => ({ value }))

export default function DeleteVmSnapshot({ rows, onClose }: DeleteImageProps) {
  const classes = useStyles()
  const [showWarning, setShowWarning] = useState(false)
  const [glanceHostNotReachable, setGlanceHostNotReachable] = useState(false)
  const [isDeleteAssociatedVolSnapshots, setIsDeleteAssociatedVolSnapshots] = useState(true)

  const { reload: reloadVolumeSnapshots, loading: loadingVolumeSnapshots } = useListAction(
    listVolumeSnapshots,
  )
  const volumeSnapshotsById = useSelector(volumeSnapshotsByIdSelector)

  const hasSnapshots = useMemo(() => {
    return rows?.some((row) => row?.imageType === 'vmVolumeSnapshot')
  }, [rows])

  // Get all associated volume snapshots for all selected images using volumeSnapshotsById
  const associatedVolumeSnapshots = useMemo(() => {
    return rows
      ?.flatMap((image) => image?.snapshotIds) // get all snapshotIds for all selected images
      ?.map((snapshotId) => volumeSnapshotsById?.[snapshotId])
      ?.filter(Boolean) //filter out null values
  }, [rows, volumeSnapshotsById])

  // Initialize image results with names
  const [imageResults, setImageResults] = useState<ActionStateResult[]>(
    initializeResults(rows, 'Deleting snapshot', null),
  )

  // Initialize volume snapshot results with names from volumeSnapshotsById
  // With glance API handling volume snapshot deletion, we don't need
  // to manually trigger volume snapshot deletion & display results
  const [volumeSnapshotResults, setVolumeSnapshotResults] = useState<ActionStateResult[]>(
    initializeResults(associatedVolumeSnapshots, 'Deleting Volume Snapshot', null),
  )

  // Use separate deletion hooks for images and volume snapshots
  const { bulkRemove: bulkRemoveImages, error } = useDeleteAction(deleteImage)

  useEffect(() => {
    const asyncCall = async () => {
      try {
        const endpoint = await getHostGlanceEndpoint()
        if (!endpoint) {
          setShowWarning(true)
        }
        await testHostGlance(endpoint)
      } catch (err) {
        if (err?.err?.code === 'ECONNABORTED') {
          setGlanceHostNotReachable(true)
        }
      }
    }
    asyncCall()
  }, [])

  // Handle delete operation
  const handleDelete = useCallback(async () => {
    // Initialize image deletion
    const initialImageResults = initializeResults(rows, 'Deleting snapshot')
    setImageResults(initialImageResults)

    // Delete images first
    const imageRowsWithBody = rows.map((image) => ({
      id: image?.id,
      deleteSnapshots: image?.imageType === 'vmVolumeSnapshot' && isDeleteAssociatedVolSnapshots,
    }))

    const imageResults = await bulkRemoveImages(imageRowsWithBody)
    const processedImageResults = processResults('Snapshot', imageResults, rows)
    setImageResults(processedImageResults)
  }, [
    rows,
    bulkRemoveImages,
    associatedVolumeSnapshots,
    isDeleteAssociatedVolSnapshots,
    initializeResults,
    processResults,
  ])

  const showFinishButton = useMemo(() => imageResults?.some((r) => !!r?.status), [imageResults])

  const handleClose = useCallback(
    (success?: boolean) => {
      if (showFinishButton) {
        reloadVolumeSnapshots(true)
      }
      setImageResults([])
      setVolumeSnapshotResults([])
      onClose(success)
    },
    [onClose, showFinishButton, reloadVolumeSnapshots],
  )

  const data = useMemo(
    () => getVolumeSnapshotsGridData({ volumeSnapshotResults, rows, imageResults }),
    [rows, volumeSnapshotResults, imageResults],
  )

  const columns = useMemo(
    () => [
      {
        key: 'name',
        label: 'Volume Snapshot',
      },
      ...(rows?.length > 1 ? [{ key: 'vmSnapshotName', label: 'VM Snapshot' }] : []),
      {
        key: 'vmSnapshotDeleted',
        label: 'Status',
        CellComponent: ({ value, item }) => {
          if (!value) return null

          return (
            <Text variant="body2">
              <FontAwesomeIcon className={classes.successIcon}>circle-check</FontAwesomeIcon>
              Deleted
            </Text>
          )
        },
      },
    ],
    [rows, classes],
  )

  return (
    <Modal
      panel="dialog"
      open
      onClose={handleClose}
      title="Delete Snapshot(s)"
      maxWidth={hasSnapshots ? 800 : 600}
      footer={
        <>
          {error && <Alert variant="error" title={error?.title} message={error?.message} />}
          {showFinishButton ? (
            <Button variant="secondary" onClick={handleClose}>
              Finish
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleDelete} disabled={glanceHostNotReachable}>
                Confirm
              </Button>
            </>
          )}
        </>
      }
    >
      <>
        <ImageServiceStatusMessage glanceHostNotReachable={glanceHostNotReachable} />
        {/* Images Section */}
        <ShowActionState
          title={
            showWarning
              ? 'You are about to delete snapshot(s) that belong to a host without an Image Library role. This action cannot be undone.'
              : 'This will permanently delete the following snapshot(s):'
          }
          results={imageResults}
          customClassName={classes.showImagesActionState}
        />
        {hasSnapshots && (
          <>
            <Checkbox
              label="Delete all the associated volume snapshots"
              checked={isDeleteAssociatedVolSnapshots}
              onChange={() => setIsDeleteAssociatedVolSnapshots(!isDeleteAssociatedVolSnapshots)}
              id="deleteAllVolumeSnapshots"
            />
            <Info className={clsx(classes.box, classes.warning)} error>
              {isDeleteAssociatedVolSnapshots
                ? 'The associated volume snapshot(s) for the selected VM snapshot(s) will be deleted as part of this operation. Uncheck the checkbox if you wish to retain them.'
                : 'The associated volume snapshot(s) for the selected VM snapshot(s) will be retained.'}
            </Info>
            {isDeleteAssociatedVolSnapshots && (
              <div className={classes.grid}>
                <Grid
                  uniqueIdentifier="id"
                  columns={columns}
                  data={data}
                  label="Volume Snapshots"
                  orderBy="name"
                  orderDirection="asc"
                  emptyContent="No associated volume snapshots found"
                  disableRowSelection={true}
                  disableColumnHiding
                  rowsPerPage={3}
                  pageSizeOptions={pageSizeOptions}
                  loading={loadingVolumeSnapshots}
                />
              </div>
            )}
          </>
        )}
      </>
    </Modal>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  errorMessageContainer: {
    gap: theme.spacing(1),
  },
  showActionState: {
    display: 'grid',
    gridTemplateColumns: '250px max-content',
  },
  showImagesActionState: {
    display: 'grid',
    gridTemplateColumns: '8px 1fr 1fr',
  },
  grid: {
    marginBottom: theme.spacing(2),
  },
  failedIcon: {
    color: theme.components.badge.error.color,
    marginRight: 8,
  },
  successIcon: {
    color: theme.components.badge.success.color,
    marginRight: 8,
  },
  warning: {
    border: `1px solid ${theme.palette.yellow[500]}`,
    background: theme.palette.yellow[100],
  },
}))
